import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  Box,
  Flex,
  Text,
  Radio,
  RadioGroup,
  Button,
} from '@chakra-ui/react';
import { StatusColors, StatusType } from '../../constants/statusColors.ts';
import AleaCheckIcon from '../../icons/AleaCheckIcon.tsx';
import { useTranslation } from 'react-i18next';
import AleaLoader from '../../components/AleaLoader/AleaLoader.tsx';

interface AleaStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  residentName: string;
  currentStatus: StatusType;
  onConfirm: (newStatus: StatusType) => void;
}

const VISIBLE_STATUSES: StatusType[] = [
  'Enabled',
  'Out Of Care',
  'In Hospital',
  'Disabled',
];

const STATUS_TRANSLATION_KEYS: Record<StatusType, string> = {
  'Enabled': 'statusActive',
  'Out Of Care': 'Out Of Care',
  'In Hospital': 'In Hospital',
  'Disabled': 'statusInactive',
  'Archived': 'Archived',
};

const AleaStatusModal: React.FC<AleaStatusModalProps> = ({
  isOpen,
  onClose,
  residentName,
  currentStatus,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<StatusType>(currentStatus);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelected(currentStatus);
    }
  }, [isOpen, currentStatus]);

  // Lower header z-index while modal is open
  useEffect(() => {
    try {
      const ev = new CustomEvent('alea-global-modal-open', { detail: isOpen });
      window.dispatchEvent(ev);
    } catch {}
    return () => {
      try {
        const ev = new CustomEvent('alea-global-modal-open', { detail: false });
        window.dispatchEvent(ev);
      } catch {}
    };
  }, [isOpen]);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm(selected);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRowActivate = (status: StatusType) => {
    setSelected(status);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered closeOnOverlayClick={!isSubmitting} closeOnEsc={!isSubmitting}>
      <ModalOverlay zIndex={1500} />
      <ModalContent width="400px" minWidth="400px" borderRadius="20px" bg="white" zIndex={1501}>
        {isSubmitting && <AleaLoader />}
        <Box px="24px" pt="20px" pb="25px">
          <Text fontSize="15px" color="#000" fontWeight={300}>
            {residentName}
          </Text>
          <Text fontSize="20px" fontWeight={500} color="#000">
            {t('adaptStatus')}
          </Text>
        </Box>

        <Box
          px="16px"
          pb="25px"
          sx={{
            '.chakra-radio__control': {
              width: '20px',
              height: '20px',
            },
            '.chakra-radio input:not(:checked) + .chakra-radio__control': {
              borderWidth: '2px',
              borderStyle: 'solid',
              borderColor: 'alea.500 !important',
            },
          }}
        >
          <RadioGroup value={selected} onChange={(v) => setSelected(v as StatusType)}>
            <Box border="1px solid #E6E6E6" borderRadius="20px" overflow="hidden">
              {VISIBLE_STATUSES.map((status, index) => (
                <Flex
                  key={status}
                  alignItems="center"
                  justifyContent="space-between"
                  px="16px"
                  py="14px"
                  borderTopWidth={index === 0 ? 0 : '1px'}
                  borderColor="#EDEDED"
                  bg="white"
                  cursor="pointer"
                  onClick={() => handleRowActivate(status)}
                  role="radio"
                  aria-checked={selected === status}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleRowActivate(status);
                    }
                  }}
                >
                  <Flex alignItems="center" gap="12px">
                    <Radio
                      value={status}
                      colorScheme="alea"
                      pointerEvents="none"
                      sx={{
                        '& .chakra-radio__control': {
                          width: '20px',
                          height: '20px',
                        },
                        '& input:not(:checked) + .chakra-radio__control': {
                          borderWidth: '2px',
                          borderStyle: 'solid',
                          borderColor: 'alea.500 !important',
                        },
                        '& input:focus + .chakra-radio__control': {
                          borderColor: 'alea.500 !important',
                          boxShadow: '0 0 0 1px var(--chakra-colors-alea-500)',
                        },
                      }}
                    />
                    <Text fontSize="18px" color="#000">{t(STATUS_TRANSLATION_KEYS[status])}</Text>
                  </Flex>
                  <Box
                    bg={StatusColors[status]}
                    borderRadius="50%"
                    width="24px"
                    height="24px"
                  />
                </Flex>
              ))}
            </Box>
          </RadioGroup>
        </Box>

        <Flex alignItems="center" justifyContent="end" px="16px" pb="20px">
          <Button variant="link" color="alea.500" onClick={onClose} mr="15px" fontSize="18px" fontWeight={400} textDecoration="underline">
            {t('general.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            bg="#23424B"
            color="white"
            _hover={{ bg: '#23424B' }}
            height="48px"
            borderRadius="20px"
            px="18px"
            w="100px"
          >
            <AleaCheckIcon color='white' size='29' />
          </Button>
        </Flex>
      </ModalContent>
    </Modal>
  );
};

export default AleaStatusModal;


